import { mapGetters, mapMutations } from 'vuex';
import { convertCodeToValue } from '@/utils/CRMUtils';
import { isEmptyString } from '@/utils';
import { ProductExcessType, AcceptableThisEligibilityType, InsurableTypeEnum, NonDependantFlexCodes } from '@/enums';
import { calculateAge, reverseFormatFormDate, DHLError } from '@general/js/utils';
import { CoverComposition, State, CoverTypeValue, ProductType, Eligibility, WhoIsEligible, DOBErrorMessage } from '@general/js/enums';
import { DEFAULT_VETERAN_INFO } from '@general/js/templates/JoinNow/JoinNowSteps/EligibilityTypes';
import { VUEX_CAPTURE_ADDITIONAL_INFORMATION, VUEX_CHANGE_COVER_ROOT } from '@/constants/ChangeCover/ChangeCoverVuexConstants';
import { TransferToFundReason } from '@general/js/constants';
import Services from '@/services/MyCover/ChangeCover/ChangeProduct';
import { getPartnerDetailsMP } from '@/utils/MemberUtils';
import { getSelectOptionsLeft } from '@/utils/ConcessionCardUtils';
import CommonMixins from './CommonMixins';

export default {
  mixins: [CommonMixins],

  data() {
    return {
      // Used for duplicate person check
      modalOpen: false,
      modalIndex: 0,
      apiStatus: {
        duplicatePersonCheck: {
          loading: false,
          fail: false,
        },
      },
      // CRM value options
      transferToFundValues: TransferToFundReason,
      stateOptions: [
        { code: 1, value: State.ACT },
        { code: 2, value: State.NSW },
        { code: 3, value: State.NT },
        { code: 4, value: State.QLD },
        { code: 5, value: State.SA },
        { code: 6, value: State.TAS },
        { code: 7, value: State.VIC },
        { code: 8, value: State.WA },
      ],
      hasCardOptions: [
        { code: 0, value: false },
        { code: 1, value: true },
      ],
      serviceConnectionOptions: [
        { code: 0, value: 'Partner' },
        { code: 1, value: 'Self' },
      ],
      serviceAgencyOptions: [
        { code: 0, value: 'Navy' },
        { code: 1, value: 'Airforce' },
        { code: 2, value: 'Army' },
        { code: 3, value: 'Reservists' },
      ],
      serviceTypeOptions: [
        { code: 0, value: 'Permanent' },
        { code: 1, value: 'Reservist' },
      ],
      relationshipConnectionOptions: [
        { code: 1, value: 'Child' },
        { code: 2, value: 'Parent' },
        { code: 3, value: 'Grandparent' },
        { code: 4, value: 'Brother or Sister' },
        { code: 5, value: 'Former Partner' },
      ],
      OVER_COVERAGE_ERROR_MESSAGE: `<p>There has been an error with making this policy change.</p>`,
      DUPLICATE_PERSON_ERROR_MESSAGE: `<p>There has been an error with adding this person to this policy, a Defence Health team member will contact you shortly to assist you.</p>`,
      ENFORCE_PARTNER_ERROR_MESSAGE: `<p>You are unable to proceed with removing this partner on your current policy without replacing them.</p>`,
      sercatOptions: [
        { code: 'SERCAT 3', value: '3' },
        { code: 'SERCAT 4', value: '4' },
        { code: 'SERCAT 5', value: '5' },
        { code: 'SERCAT 6', value: '6' },
        { code: 'SERCAT 7', value: '7' },
      ],
    };
  },

  computed: {
    ...mapGetters('ChangeCoverRoot', {
      referenceData: VUEX_CHANGE_COVER_ROOT.GETTERS.getReferenceData,
      memberDetails: VUEX_CHANGE_COVER_ROOT.GETTERS.getMemberDetails,
      membershipPolicy: VUEX_CHANGE_COVER_ROOT.GETTERS.getMembershipPolicy,
      eligibilityDetails: VUEX_CHANGE_COVER_ROOT.GETTERS.getEligibilityDetails,
      membershipProducts: VUEX_CHANGE_COVER_ROOT.GETTERS.getMembershipProducts,
      currentComposition: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentComposition,
      currentRebateTier: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentRebateTier,
      currentMyCircumstances: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentMyCircumstances,
      currentExcess: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentExcess,
      currentNumberOfChildren: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentChildren,
      currentState: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentState,
      currentEligibility: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentEligibility,
      currentPremium: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentPremium,
      currentCoverStartDate: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentCoverStartDate,
      memberPolicies: VUEX_CHANGE_COVER_ROOT.GETTERS.getMemberPolicies,
    }),

    ...mapGetters('ChangeCoverRoot/CaptureAdditionalInformation', {
      captureInformationReferenceData: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getCaptureInformationReferenceData,
      policyChangesStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getPolicyChangesInformation,
      policyHolderStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getPolicyHolderInformation,
      partnerStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getPartnerInformation,
      childrenStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getChildrenInformation,
      eligibilityInformationStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getEligibilityInformation,
      rebateInformationStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getRebateInformation,
      confirmationInformationStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getConfirmation,
      captureAdditionalInformationData: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getCaptureAdditionalInformationData,
      initialData: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getInitialData,
    }),

    getNeedAnalysisRecommendationsPayload() {
      const closedProductCoverType = this.getCoverTypeForClosedProduct();
      return {
        dob: this.changeCoverData.step1.DOB,
        state: this.changeCoverData.step1.state,
        eligibility: this.changeCoverData.step1.eligibility,
        composition: this.changeCoverEnteredData ? this.changeCoverEnteredData.step2.coverComposition : null,
        hospitalAndExtras:
          this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS
            ? closedProductCoverType || this.changeCoverData.step2.coverType
            : this.changeCoverData.step2.coverType,
        keepProduct: this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS ? closedProductCoverType != null : false,
        medicalNeedClinical: this.convertListToString(this.changeCoverData.step3.specificTreatmentCategory),
        medicalNeedExtras: this.convertListToString(this.changeCoverData.step3.specificTreatmentModality),
        excludePregnancy: this.changeCoverData.step3.noPregnancy,
        coverReason: this.changeCoverData.step3.coverReason,
        existingProducts: this.getExistingProducts(),
      };
    },

    hasAddressStateChanged() {
      return this.currentState !== this.changeCoverData.step1.state;
    },

    hasEligibilityChanged() {
      return this.currentEligibility !== this.changeCoverData.step1.eligibility;
    },

    isPolicyHolderEligible() {
      // below is a roundabout way of saying `whoIsEligible = 'self'`. This was needed because the enum is other way round, i.e returns number(i.e optionset value) instead of string value, so to keep using the same enum while keeping it clear that we are checking for WhoIsEligible.SELF
      return (
        this.eligibilityDetails.whoIsEligible?.toLowerCase() ===
        Object.keys(WhoIsEligible)
          .find((key) => {
            return WhoIsEligible[key] === WhoIsEligible.SELF;
          })
          .toLowerCase()
      );
    },

    oldCompositionHavePartner() {
      if (this.memberDetails) {
        const partner = getPartnerDetailsMP(this.memberDetails, true);

        if (partner) {
          return true;
        }
      }

      return false;
    },

    oldCompositionHaveChildren() {
      return [CoverComposition.SINGLE_PARENT, CoverComposition.SINGLE_PARENT_PLUS, CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS, CoverComposition.CHILDREN_ONLY].includes(
        this.currentComposition,
      );
    },

    newCompositionHavePartner() {
      return [CoverComposition.COUPLE, CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS].includes(this.changeCoverData.step2.composition);
    },

    newCompositionHaveChildren() {
      return [CoverComposition.SINGLE_PARENT, CoverComposition.SINGLE_PARENT_PLUS, CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS, CoverComposition.CHILDREN_ONLY].includes(
        this.changeCoverData.step2.composition,
      );
    },

    showNewPartnerSection() {
      if (this.oldCompositionHavePartner && this.newCompositionHavePartner) {
        return this.partnerStep.keepAdult === false;
      }
      if (!this.oldCompositionHavePartner && this.newCompositionHavePartner) {
        return true;
      }
      return false;
    },

    showPolicy() {
      const [terminatedProductCodes, currentBoth, newBoth] = this.determinePolicyChanges();
      if (terminatedProductCodes.length > 0) {
        // When the product code is changed the policy termination reason will be displayed.
        return true;
      }

      // When the enrolled products are changing from hospital and extras to hospital only or extras only, the section will be displayed.
      if (currentBoth && !newBoth) return true;

      return false;
    },

    // Show partner if going from couple cover to single cover
    showPartner() {
      const toCompositionList = [CoverComposition.CHILDREN_ONLY, CoverComposition.SINGLE, CoverComposition.SINGLE_PARENT, CoverComposition.SINGLE_PARENT_PLUS];
      // From Family+
      if (this.currentComposition === CoverComposition.FAMILY_PLUS) {
        if (toCompositionList.includes(this.myCover.composition)) return true;
      }

      // From Family
      if (this.currentComposition === CoverComposition.FAMILY) {
        if (toCompositionList.includes(this.myCover.composition)) return true;
      }

      // From Couple
      if (this.currentComposition === CoverComposition.COUPLE) {
        if (toCompositionList.includes(this.myCover.composition)) return true;
      }

      return false; // hide by default
    },

    isPartnerRebateApplicant() {
      const rebateApplicant = this.membershipPolicy.rebateApplicant;
      const partner = getPartnerDetailsMP(this.memberDetails);
      return partner?.personCode === rebateApplicant;
    },

    isPartnerNotAnInsuredAdult() {
      const partner = getPartnerDetailsMP(this.memberDetails);
      return partner?.insurableType !== InsurableTypeEnum.ADULT;
    },

    showChildren() {
      const toCompositionList = [CoverComposition.SINGLE, CoverComposition.COUPLE];
      const fromCompositionList = [
        CoverComposition.CHILDREN_ONLY,
        CoverComposition.FAMILY,
        CoverComposition.FAMILY_PLUS,
        CoverComposition.SINGLE_PARENT,
        CoverComposition.SINGLE_PARENT_PLUS,
        CoverComposition.FAMILY_PLUS,
      ];

      if (fromCompositionList.includes(this.currentComposition) && toCompositionList.includes(this.myCover.composition)) {
        return true;
      }

      return false;
    },

    eligibleForABD() {
      const products = this.selectedProducts.products.map((obj) => {
        return obj.eligibleForABD;
      });

      return products.some(Boolean) || false;
    },

    oldProductHadABD() {
      const oldProducts = this.changeCoverData.recommendedProducts.existingProducts.products.map((obj) => {
        return obj.eligibleForABD;
      });

      return oldProducts.some(Boolean) || false;
    },

    containsHospital() {
      const productTypes = this.selectedProducts.products.map((product) => {
        return product.type ? product.type.toLowerCase() : product.productType.toLowerCase();
      });
      return productTypes.some((type) => {
        return type === ProductType.HOSPITAL_ONLY || type === ProductType.PACKAGE;
      });
    },

    // If eligibility changed, return empty eligibility data, otherwise return initial eligibility data
    getEligibilityInitialData() {
      let initialData;
      if (this.currentEligibility === this.changeCoverData.step1.eligibility) {
        initialData = { ...this.updateInitialEligibility() };
      } else {
        initialData = { ...this.initialData.eligibility };
      }
      if (isEmptyString(initialData?.veteranInfo?.rank)) {
        delete initialData?.veteranInfo?.rank;
      }
      return initialData;
    },

    partnerInfoChanged() {
      if (this.oldCompositionHavePartner && this.newCompositionHavePartner) {
        return this.partnerStep.keepAdult === false;
      }
      if (!this.oldCompositionHavePartner && this.newCompositionHavePartner) {
        return true;
      }
      if (this.oldCompositionHavePartner && !this.newCompositionHavePartner) {
        return true;
      }
      return false;
    },
    multiplePolicyHolderMemberships() {
      return this.memberPolicies?.filter(this.isPolicyHolder).length > 1;
    },
    multipleMembershipsAndAtLeastOnePolicyHolder() {
      return this.memberPolicies?.length > 1 && this.memberPolicies?.some(this.isPolicyHolder);
    }
  },

  methods: {
    ...mapMutations('ChangeCoverRoot', {
      updateReferenceData: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateReferenceData,
      updateMemberDetails: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateMemberDetails,
      updateMembershipProducts: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateMembershipProducts,
      updateMembershipPolicy: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateMembershipPolicy,
      updateEligibilityDetails: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateEligibilityDetails,
      updateCoverComposition: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentComposition,
      updateCurrentRebateTier: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentRebateTier,
      updateCurrentMyCircumstances: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentMyCircumstances,
      updateCurrentExcess: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentExcess,
      updateCurrentNumberOfChildren: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentChildren,
      updateCurrentState: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentState,
      updateCurrentPremium: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentPremium,
      updateCurrentCoverStartDate: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentCoverStartDate,
      resetData: VUEX_CHANGE_COVER_ROOT.MUTATIONS.resetData,
      updateMemberPolicies: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateMemberPolicies,
    }),

    ...mapMutations('ChangeCoverRoot/CaptureAdditionalInformation', {
      updateCaptureInformationReferenceData: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateCaptureInformationReferenceData,
      updatePolicyChangesStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updatePolicyChanges,
      updatePolicyHolderStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updatePolicyHolder,
      updatePartnerStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updatePartner,
      updateChildrenStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateChildren,
      updateEligibilityStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateEligibility,
      updateRebateStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateRebate,
      updateInitialData: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialData,
      updateEligibilityInitialData: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateEligibilityInitialData,
      resetCAIData: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.resetCAIData,
    }),

    async raiseWorkItem(payload) {
      await Services.raiseDuplicatePersonWorkItem(this.memberNumber, payload);
    },

    age31OrMore(age) {
      const ageAtCoverStartDate = calculateAge(age, this.changeCoverData.step1.startDate);
      return ageAtCoverStartDate >= 31;
    },

    determinePolicyChanges() {
      const currentPolicyProducts = this.myRecommendations.existingProducts.products;
      const selectedPolicyProducts = this.selectedProducts.products;

      // Determine current products
      let currentHospital = false;
      let currentExtras = false;
      let currentBoth = false;
      let currentPackage = false;
      const currentProductCodes = [];

      currentPolicyProducts.forEach((product) => {
        if (product.productType.toLowerCase() === CoverTypeValue.HOSPITAL_ONLY) currentHospital = true;
        if (product.productType.toLowerCase() === CoverTypeValue.EXTRAS_ONLY) currentExtras = true;
        if (product.productType.toLowerCase() === ProductType.PACKAGE) currentPackage = true;
        currentProductCodes.push(product.productCode);
      });
      // Determine if current cover is both
      currentBoth = (currentHospital && currentExtras) || currentPackage;

      // Determine new products
      let newHospital = false;
      let newExtras = false;
      let newBoth = false;
      let newPackage = false;
      const newProductCodes = [];

      selectedPolicyProducts.forEach((product) => {
        if (product.productType.toLowerCase() === CoverTypeValue.HOSPITAL_ONLY) newHospital = true;
        if (product.productType.toLowerCase() === CoverTypeValue.EXTRAS_ONLY) newExtras = true;
        if (product.productType.toLowerCase() === ProductType.PACKAGE) newPackage = true;
        newProductCodes.push(product.productCode);
      });
      // Determine if new cover is both
      newBoth = (newHospital && newExtras) || newPackage;

      const terminatedProductCodes = currentProductCodes.filter((p) => {
        return !newProductCodes.includes(p);
      });
      this.terminatedProductCodes = terminatedProductCodes;

      return [terminatedProductCodes, currentBoth, newBoth];
    },

    updateInitialEligibility() {
      let eligibility = {};

      // Service connection
      if (this.eligibilityDetails.whoIsEligible) {
        eligibility.serviceConnection = this.eligibilityDetails.whoIsEligible;
      }
      // Partner name
      if (this.eligibilityDetails.partnersName) {
        eligibility.serviceConnectionName = this.eligibilityDetails.partnersName;
      }

      if (this.currentEligibility === AcceptableThisEligibilityType.SERVING || this.currentEligibility === AcceptableThisEligibilityType.EX_SERVING) {
        eligibility = { ...eligibility, veteranInfo: { ...DEFAULT_VETERAN_INFO } };
        // Rank
        if (this.eligibilityDetails.rank) {
          eligibility.veteranInfo.rank = this.eligibilityDetails.rank;
        }
        // Service agency
        if (this.eligibilityDetails.currentlyServing) {
          eligibility.veteranInfo.serviceAgency = this.eligibilityDetails.currentlyServing;
        }
        // Service type
        if (this.eligibilityDetails.exServing) {
          eligibility.veteranInfo.serviceType = this.eligibilityDetails.exServing;
        }
        // Date of enlistment
        if (this.eligibilityDetails.dateOfEnlistment) {
          eligibility.enlistmentDate = reverseFormatFormDate(this.eligibilityDetails.dateOfEnlistment);
        }

        // Declaration, Boolean
        if (this.eligibilityDetails.eligibilityDeclaration) {
          eligibility.declaration = Boolean(this.eligibilityDetails.eligibilityDeclaration);
        }
      }

      if (this.currentEligibility === AcceptableThisEligibilityType.SERVING) {
        // Sercat status
        if (this.eligibilityDetails.sercatStatus) {
          const sercatValue = this.eligibilityDetails.sercatStatus;
          eligibility.sercatStatus = convertCodeToValue(this.sercatOptions, sercatValue);
        }
        // Sercat file names
        eligibility.sercatFileNames = [];
      }

      // ADF declaration expiry date when Currently serving
      if (this.eligibilityDetails.adfDeclarationExpiryDate) {
        eligibility.declarationExpiryDate = reverseFormatFormDate(this.eligibilityDetails.adfDeclarationExpiryDate);
      }

      // Eligibility declaration accepted
      if (this.eligibilityDetails.eligibilityDeclaration) {
        eligibility.declaration = Boolean(this.eligibilityDetails.eligibilityDeclaration);
      }
      // Eligibility declaration date
      if (this.eligibilityDetails.eligibilityDeclarationDate) {
        eligibility.declarationDate = reverseFormatFormDate(this.eligibilityDetails.eligibilityDeclarationDate);
      }
      // Discharge date only for ExServing
      if (this.currentEligibility === AcceptableThisEligibilityType.EX_SERVING) {
        // Date of discharge
        if (this.eligibilityDetails.dateOfDischarge) {
          eligibility.dischargeDate = reverseFormatFormDate(this.eligibilityDetails.dateOfDischarge);
        }
      }

      if (this.currentEligibility === AcceptableThisEligibilityType.WORK_CONNECTION || this.currentEligibility === AcceptableThisEligibilityType.RELATED) {
        // Connection type
        if (this.eligibilityDetails.howAreTheyEligible) {
          eligibility.connectionType = this.eligibilityDetails.howAreTheyEligible;
        }
        // Company name / Connection name
        if (this.eligibilityDetails.companyName) {
          eligibility.connectionName = this.eligibilityDetails.companyName;
        }

        // Department name (can be available for both eligibilities: Relatives & Work connection)
        if (eligibility.connectionType === Eligibility.DPS) {
          // Department name for Defense Public Service eligibility type
          if (this.eligibilityDetails.departmentName) {
            eligibility.connectionName = this.eligibilityDetails.departmentName;
          }
        }
      }

      if (this.currentEligibility === AcceptableThisEligibilityType.WORK_CONNECTION) {
        // Relation name / Name of person with work connection
        if (this.eligibilityDetails.nameOfPersonWithWorkConnection) {
          eligibility.relationName = this.eligibilityDetails.nameOfPersonWithWorkConnection;
        }
      }

      // Relatives connection type values
      if (this.currentEligibility === AcceptableThisEligibilityType.RELATED) {
        eligibility = { ...eligibility, veteranInfo: {} };
        // Relationship
        if (this.eligibilityDetails.whatIsRelationshipConnection) {
          eligibility.relationship = this.eligibilityDetails.whatIsRelationshipConnection;
        }
        // Name of connected individual / relative
        if (this.eligibilityDetails.nameOfConnectedIndividual) {
          eligibility.relationName = this.eligibilityDetails.nameOfConnectedIndividual;
        }
        // Rank of relative
        if (this.eligibilityDetails.rankRelative) {
          eligibility.veteranInfo.rank = this.eligibilityDetails.rankRelative;
        }
        // Service of relative
        if (this.eligibilityDetails.serveInRelative) {
          eligibility.veteranInfo.serviceAgency = this.eligibilityDetails.serveInRelative;
        }
        // Service type of relative
        if (this.eligibilityDetails.theirServiceIs) {
          eligibility.veteranInfo.serviceType = this.eligibilityDetails.theirServiceIs;
        }
      }

      return eligibility;
    },

    getVeteranCardReturnValue(veteranCardOptions, displayValue) {
      if (veteranCardOptions) {
        for (let index = 0; index < veteranCardOptions.length; index++) {
          if (veteranCardOptions[index].display === displayValue) {
            return veteranCardOptions[index].returnValue;
          }
        }
      }
      return null;
    },

    getVeteranCardDisplayValue(veteranCardOptions, returnValue) {
      if (veteranCardOptions) {
        for (let index = 0; index < veteranCardOptions.length; index++) {
          if (veteranCardOptions[index].returnValue === returnValue) {
            return veteranCardOptions[index].display;
          }
        }
      }
      return null;
    },

    getDOBErrorMessage(dobField) {
      const errors = dobField.$errors || [];
      if (errors.some(e => e.$validator === 'required')) return `${DOBErrorMessage.IS_EMPTY} Date of birth.`;
      if (errors.some(e => e.$validator === 'isPastDate') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) return DOBErrorMessage.IS_FUTURE_DATE;
      if (errors.some(e => e.$validator === 'overAge') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) return DOBErrorMessage.IS_OVERAGE;
      if (errors.some(e => e.$validator === 'underAge') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) return DOBErrorMessage.IS_UNDERAGE;
      return DOBErrorMessage.IS_INVALID;
    },

    getFundErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'required')) return 'Please include a response for Member number.';
      if (errors.some(e => e.$validator === 'maxLength' || e.$validator === 'stringWithSymbols')) return 'Please enter a combination of up to 19 letters or numbers.';
      return '';
    },

    getConcessionNumberErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'required')) return 'Please provide a response for Card number.';
      if (errors.some(e => e.$validator === 'invalidConcessionCardNumber')) return 'Please enter 9 numbers and a letter.';
      return '';
    },

    getConcessionExpiryErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'IS_INVALID')) return 'Please enter the expiry date using the format DD/MM/YYYY.';
      if (errors.some(e => e.$validator === 'isPastDate')) return 'Please try again, expiry dates must be a future date.';
      return '';
    },

    getInitialMyCircumstances() {
      const myCircumstances = this.membershipPolicy.myCircumstances;
      this.updateCurrentMyCircumstances(myCircumstances);
    },

    getInitialRebateTierLevel() {
      const rebateTier = this.membershipPolicy.rebateTier;
      this.updateCurrentRebateTier(rebateTier);
    },

    getRebateApplicant() {
      // PersonCode e.g. '4854'
      return this.membershipPolicy.rebateApplicant;
    },

    getProductExcessType(products) {
      const foundProduct = products.find((product) => {
        return [(ProductExcessType.POLICY_EXCESS, ProductExcessType.MEMBER_EXCESS)].includes(product.productExcessType);
      });

      return foundProduct ? foundProduct.productExcessType : null;
    },

    isCompositionHaveChildren(composition) {
      return [CoverComposition.FAMILY, CoverComposition.SINGLE_PARENT, CoverComposition.CHILDREN_ONLY, CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT_PLUS].includes(
        composition,
      );
    },

    getDropdownOption(fullOptionList, currentValue, defaultValue) {
      const dropdownOption = fullOptionList.find((option) => {
        return option.value === currentValue;
      });

      if (dropdownOption && dropdownOption.name) {
        return dropdownOption.name;
      }
      return defaultValue;
    },

    openModal(index) {
      if (index) {
        // modal index to know which child called the modal
        this.modalIndex = index;
      }
      this.modalOpen = true;
    },

    async duplicatePersonSearch(firstName, lastName, gender, DOB) {
      this.apiStatus.duplicatePersonCheck.loading = true;
      this.apiStatus.duplicatePersonCheck.fail = false;

      try {
        return await Services.isPersonDuplicate(firstName, lastName, gender, DOB);
      } catch (error) {
        logger.error('Error checking for duplicate person: ', error);
        this.apiStatus.duplicatePersonCheck.fail = true;
        // default failsafe
        throw new DHLError(error);
      } finally {
        this.apiStatus.duplicatePersonCheck.loading = false;
      }
    },

    /**
     * Returns whether there is an overcoverage of the current person
     * @param {array} duplicatePersonProducts product array containing all products the person is already on
     * @param {array} selectedProducts product array containing all products that policy is being changed to
     */
    overCoverageCheck(duplicatePersonProducts, selectedProducts) {
      // if person is package already, adding any products will be overcovered
      const personContainsPackage = duplicatePersonProducts.some((product) => {
        return product.type.toLowerCase() === ProductType.PACKAGE;
      });
      if (personContainsPackage) {
        return true;
      }

      // if the selected product is a package, need to check if duplicate person had any products. If they did, overcovered.
      const selectedProductContainsPackage = selectedProducts.some((product) => {
        return product.productType.toLowerCase() === ProductType.PACKAGE;
      });
      if (selectedProductContainsPackage && duplicatePersonProducts.length > 0) {
        return true;
      }

      // if both the duplicate person and current selected products has hospital product, it is overcovered
      const personContainsHospital = duplicatePersonProducts.some((product) => {
        return product.type.toLowerCase() === ProductType.HOSPITAL_ONLY;
      });
      const selectedProductContainsHospital = selectedProducts.some((product) => {
        return product.productType.toLowerCase() === ProductType.HOSPITAL_ONLY;
      });
      if (personContainsHospital && selectedProductContainsHospital) {
        return true;
      }

      // if both the duplicate person and current selected products has extras product, it is overcovered
      const personContainsExtras = duplicatePersonProducts.some((product) => {
        return product.type.toLowerCase() === ProductType.EXTRAS_ONLY;
      });
      const selectedProductContainsExtras = selectedProducts.some((product) => {
        return product.productType.toLowerCase() === ProductType.EXTRAS_ONLY;
      });
      if (personContainsExtras && selectedProductContainsExtras) {
        return true;
      }

      return false;
    },

    isFundAutomaticTransfer(code) {
      const fund = this.fundNamesNewPerson.find((f) => {
        return f.value === code;
      });
      if (fund && fund.isAutomaticTransfer) {
        return fund.isAutomaticTransfer;
      }
      return false;
    },

    isPolicyHolder(membership) {
      return membership.relationship === NonDependantFlexCodes.POLICY_HOLDER;
    },

    convertCodeToValue,
    isEmptyString,
    getSelectOptionsLeft,
  },
};
